import './AssignPlaylistToPlaylistGroupOverlay.scss';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { BRAND_LETTER } from '../../js/config/_global';

import { useTranslation } from 'react-i18next';
import {
    ORDER_BY_DIRECTION,
    RESPONSE,
    SEARCH_DEBOUNCE_VALUE
} from '../../js/_constants';
import {
    sanitizeCollectionResponse
} from '../../js/helpers';
import {type Playlist, PlaylistGroup} from '../../api/types/index';

import InputField from '../inputField/InputField';
import ItemCard from '../ItemCard/ItemCard';
import { type CollectionResponse } from '../../api/types/response';
import { useFetch } from '../../hooks/useFetch';
import _server from '../../js/config/_server';
import { type SanitizedCollection } from '../../js/helpers';
import Loader from '../loader/Loader';
import debounce from 'debounce';
import { isEmpty } from 'ramda';

type AssignPlaylistToPlaylistGroupOverlayProps = {
    isFilterable: boolean;
    onItemClick: (playlistIri: string) => void;
    playlistGroup: PlaylistGroup,
    market?:boolean,
};

type SanitizerFunction = (
    collection: CollectionResponse<Playlist>
) => SanitizedCollection<Playlist>;

const AssignPlaylistToPlaylistGroupOverlay = ({
    isFilterable,
    onItemClick,
    playlistGroup,
    market,
}: AssignPlaylistToPlaylistGroupOverlayProps) => {
    const { t } = useTranslation();
    const [pendingIri, setPendingIri] = useState('');

    const {
        data: playlists,
        isLoading,
        refetchData
    } = useFetch<CollectionResponse<Playlist>, SanitizerFunction>(
        _server.services.playlists.userPlaylists,
        {
            params: {
                createdByCurrentUser: market ? false : true,
                brand: BRAND_LETTER,
                'order[updatedAt]': ORDER_BY_DIRECTION.DESC,
                playlistGroup: playlistGroup.uuid
            },
            dataFormatter: (collection: CollectionResponse<Playlist>) =>
                sanitizeCollectionResponse<Playlist>(collection)
        }
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        refetchData({
            params: {
                createdByCurrentUser: market ? false : true,
                'order[updatedAt]': ORDER_BY_DIRECTION.DESC,
                playlistGroup: playlistGroup["@id"],
                ...(value && {title: value})

            },
            dataFormatter: (collection: CollectionResponse<Playlist>) =>
                sanitizeCollectionResponse<Playlist>(collection)
        });
    };

    const debouncedHandleSearch = debounce(handleSearch, SEARCH_DEBOUNCE_VALUE);

    const handleAddPlaylistToDevice = playlistIri => onItemClick(playlistIri);

    const isSelected = item =>
        playlistGroup.items.findIndex(
            selectedPlaylist =>
                selectedPlaylist[RESPONSE.ID] === item[RESPONSE.ID]
        ) > -1;

    const handlePlaylistAssignment = (itemIri: string) => {
        setPendingIri(itemIri);
        handleAddPlaylistToDevice(itemIri);
    };

    const handlePlaylistCardClick = (playlistId: string) => {
        handlePlaylistAssignment(playlistId);
    };

    useEffect(() => {
        setPendingIri('');
    }, [playlistGroup.items]);

    const renderPlaylists = () => {

        if (isEmpty(playlists?.list)) {
            return (
                <span className="empty-playlists">
                    {t('device_detail_playlist_overlay_no_playlists')}
                </span>
            );
        }
        return playlists?.list.map((item,index) => {
            const itemClasses = classNames({
                'is-selected': isSelected(item),
                'playlist-card': true
            });
            return (
                <li key={item[RESPONSE.ID]}>
                    <ItemCard
                        key={item[RESPONSE.ID]}
                        className={itemClasses}
                        thumbnail={item.thumbnail}
                        icon={isSelected(item) ? "select-circle" : "media-playlist" }
                        title={item.title}
                        isLoading={pendingIri === item[RESPONSE.ID]}
                        onClick={() =>
                            handlePlaylistCardClick(item[RESPONSE.ID])
                        }
                        data-testid={`playliat ${index + 1}`}
                    />
                </li>
            );
        });
    };

    return (
        <div
            className={classNames('b-assign-playlist-to-playlist-group-overlay', {
                'is-loading': !!pendingIri
            })}
        >
            {isFilterable && (
                <InputField
                    label={t('searchInput')}
                    name="searchInput"
                    onChangeHandler={debouncedHandleSearch}
                />
            )}
            <ul className="playlists-list">
                {isLoading ? <Loader /> : renderPlaylists()}
            </ul>
        </div>
    );
};

export default AssignPlaylistToPlaylistGroupOverlay;
